// Per-probe series on one time axis for uPlot (TimeSeries). Probes sample at
// their own instants, so each series has holes at the other probes' times;
// uPlot joins a line across undefined and breaks it only at null.
import uPlot from "uplot";

/** [time in ms, value]; a null value is a sample that has none (it failed). */
export type Point = [number, number | null];

/** A gap longer than this many usual steps means the probe sent nothing: the line breaks. */
export const GAP_FACTOR = 2.5;

/** How many steps on each side of a gap set the usual step it is compared with. */
const NEIGHBOURS = 3;

const NULL_EXPAND = 2 as uPlot.JoinNullMode;

/** The lower median of the positive values, or null when there are none. */
function lowerMedian(values: number[]): number | null {
  const v = values.filter((x) => x > 0).sort((a, b) => a - b);
  return v.length ? v[Math.floor((v.length - 1) / 2)] : null;
}

/**
 * A probe's points, in time order, with a null in every gap longer than
 * GAP_FACTOR times its usual step: no rows means no data, so the line must
 * not join across it. The usual step is the check interval for raw data and
 * the bucket for rollups, so no resolution needs configuring. It is taken
 * from the steps next to the gap, on whichever side they are longer, so an
 * interval changed inside the window is not a gap. One missed sample (twice
 * the step) stays joined.
 */
export function breakGaps(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0]);
  const steps = sorted.slice(1).map((p, i) => p[0] - sorted[i][0]);
  const out: Point[] = sorted.slice(0, 1);
  for (let i = 1; i < sorted.length; i++) {
    const s = i - 1; // steps[s] is the gap before sorted[i]
    const before = lowerMedian(steps.slice(Math.max(0, s - NEIGHBOURS), s));
    const after = lowerMedian(steps.slice(s + 1, s + 1 + NEIGHBOURS));
    const usual = before === null ? after : after === null ? before : Math.max(before, after);
    if (usual !== null && steps[s] > usual * GAP_FACTOR) out.push([(sorted[i - 1][0] + sorted[i][0]) / 2, null]);
    out.push(sorted[i]);
  }
  return out;
}

/**
 * The series on one x axis (seconds). Holes at other probes' times stay
 * undefined and are joined across; a null (a failed sample, or a gap from
 * breakGaps) is widened over the holes next to it, so the line breaks from
 * the last real sample before it to the first one after.
 */
export function alignSeries(series: Point[][]): uPlot.AlignedData {
  if (series.length === 0) return [[]];
  const tables = series.map((pts) => {
    const b = breakGaps(pts);
    return [b.map((p) => p[0] / 1000), b.map((p) => p[1])] as uPlot.AlignedData;
  });
  return uPlot.join(tables, tables.map(() => [NULL_EXPAND, NULL_EXPAND]));
}

function joinsTo(ys: ArrayLike<number | null | undefined>, i: number, dir: 1 | -1): boolean {
  let j = i + dir;
  while (j >= 0 && j < ys.length && ys[j] === undefined) j += dir;
  return j >= 0 && j < ys.length && typeof ys[j] === "number";
}

/**
 * Indices of values no line segment reaches (between gaps, or a series of one
 * sample). Lines are drawn without point markers, so these need a dot or they
 * would not show at all.
 */
export function lonelyPoints(ys: ArrayLike<number | null | undefined>): number[] {
  const out: number[] = [];
  for (let i = 0; i < ys.length; i++) {
    if (typeof ys[i] === "number" && !joinsTo(ys, i, -1) && !joinsTo(ys, i, 1)) out.push(i);
  }
  return out;
}
